import React, { useState } from "react";
import customFieldsService from "../services/customFieldsService";
import { CustomFieldDefinition, CustomFieldType } from "../types/customFields";
import { FIELD_TYPE_LABELS } from "./CustomFieldInput";

interface InlineFieldCreatorProps {
  onFieldCreated: (def: CustomFieldDefinition) => void;
  onCancel: () => void;
}

const InlineFieldCreator: React.FC<InlineFieldCreatorProps> = ({ onFieldCreated, onCancel }) => {
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<CustomFieldType>("text");
  const [newChoices, setNewChoices] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!newName.trim()) return;

    setCreating(true);
    setError("");
    try {
      const options: { choices?: string[] } = {};
      if (newType === "select") {
        const choices = newChoices.split(",").map((c) => c.trim()).filter(Boolean);
        if (choices.length === 0) {
          setError("Select fields must have at least one choice");
          setCreating(false);
          return;
        }
        options.choices = choices;
      }

      const def = await customFieldsService.createDefinition(newName.trim(), newType, options);
      onFieldCreated(def);
    } catch (err: any) {
      setError(err.response?.data?.messages?.join(", ") || "Failed to create field");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        padding: "12px",
        backgroundColor: "#f9fafb",
        borderRadius: "6px",
        border: "1px solid #e5e7eb",
      }}
    >
      {error && <div style={{ color: "red", fontSize: "13px", marginBottom: "8px" }}>{error}</div>}
      <div style={{ display: "flex", gap: "8px", marginBottom: newType === "select" ? "8px" : "0", flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Field name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{
            flex: 1,
            minWidth: "120px",
            padding: "6px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        />
        <select
          value={newType}
          onChange={(e) => setNewType(e.target.value as CustomFieldType)}
          style={{
            padding: "6px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          style={{
            padding: "6px 12px",
            backgroundColor: "#000",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: creating ? "not-allowed" : "pointer",
            fontSize: "13px",
            opacity: creating || !newName.trim() ? 0.5 : 1,
          }}
        >
          {creating ? "Creating..." : "Create"}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: "6px 12px",
            backgroundColor: "#fff",
            color: "#374151",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          Cancel
        </button>
      </div>
      {newType === "select" && (
        <input
          type="text"
          placeholder="Choices (comma-separated, e.g. Option A, Option B)"
          value={newChoices}
          onChange={(e) => setNewChoices(e.target.value)}
          style={{
            width: "100%",
            padding: "6px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
            boxSizing: "border-box",
          }}
        />
      )}
    </div>
  );
};

export default InlineFieldCreator;
